import {
  MetricThresholdColor,
  ThresholdResult as Result,
} from '@agilelab/plugin-wb-governance-common';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { Box, makeStyles, Theme, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { CSSProperties, FC, useMemo } from 'react';
import { useDefaultThresholdColors } from '../../hooks';
import { KoFilledIcon } from '../TestStatus/Icons/Icons';
import { thresholdValueToString } from './utils/ThresholdValueToString';
import { WbTooltip } from '../WbTooltip';
import { WbBadge, WbBadgeVariantProps } from '../WbBadge';
import { capitalize } from 'lodash';
import { WitboostPaletteColor } from '../../themes';

interface Props {
  value: number;
  result?: Result | null;
  hasError?: boolean;
  dimension?: number;
  style?: CSSProperties;
  variant?: 'default' | 'reduced';
}

const useStyles = makeStyles<
  Theme,
  { dimension: number; backgroundColor: string }
>(theme => ({
  icon: {
    width: ({ dimension }) => dimension,
    minWidth: ({ dimension }) => dimension,
    height: ({ dimension }) => dimension,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
  },
  defaultBox: {
    display: 'flex',
    gap: '4px',
    width: 'fit-content',
    borderRadius: '4px',
    padding: '4px',
  },
  resultBox: {
    backgroundColor: ({ backgroundColor }) => backgroundColor,
    color: ({ backgroundColor }) =>
      theme.palette.getContrastText(backgroundColor),
    fontSize: theme.typography.body1.fontSize,
  },
}));

const DEFAULT_BADGE_COLORS: Record<
  ReturnType<
    typeof useDefaultThresholdColors
  >['defaultThresholdColors'][number]['label'],
  WitboostPaletteColor
> = {
  Yellow: 'warning',
  Red: 'error',
  Green: 'success',
};

export const ThresholdResult: FC<Props> = ({
  value,
  result,
  hasError = false,
  dimension = 24,
  style,
  variant = 'default',
}) => {
  const theme = useTheme();
  const { defaultThresholdColors } = useDefaultThresholdColors();

  const configApi = useApi(configApiRef);

  const ICON_DEFAULT_COLOR = theme.palette.primary.main;

  const BADGE_DEFAULT_COLOR: WitboostPaletteColor = 'primary';

  const userDefinedColors: MetricThresholdColor[] | undefined =
    configApi.getOptional('mesh.governance.metrics.thresholds.colors');

  const resultLabel = useMemo(() => {
    if (result?.label) return result?.label;

    const defaultLabel =
      userDefinedColors?.find(c => c.label === result?.labelColor)
        ?.defaultLabel ?? 'evaluated';
    return defaultLabel;
  }, [userDefinedColors, result?.label, result?.labelColor]);

  const iconBackgroundColor: string = useMemo(() => {
    const relatedColor =
      userDefinedColors?.find(c => c.label === result?.labelColor)?.value ??
      defaultThresholdColors?.find(c => c.label === result?.labelColor)?.value;
    return relatedColor || ICON_DEFAULT_COLOR;
  }, [userDefinedColors, result, ICON_DEFAULT_COLOR, defaultThresholdColors]);

  const badgeProps: WbBadgeVariantProps = useMemo(() => {
    const label = result?.labelColor;
    if (!label) return { color: BADGE_DEFAULT_COLOR };
    const relatedColor = userDefinedColors?.find(
      c => c.label === result?.labelColor,
    )?.value;
    if (relatedColor) return { variant: 'custom', color: relatedColor };
    return {
      color: DEFAULT_BADGE_COLORS[label as keyof typeof DEFAULT_BADGE_COLORS],
    };
  }, [userDefinedColors, result, BADGE_DEFAULT_COLOR]);

  const classes = useStyles({
    dimension,
    backgroundColor: iconBackgroundColor,
  });

  return hasError ? (
    <WbTooltip title="An error occurred">
      <span>
        {variant === 'reduced' ? (
          <KoFilledIcon
            htmlColor={theme.palette.error.main}
            className={classes.icon}
            style={style}
          />
        ) : (
          <WbBadge color="error" startIcon="KO">
            Error
          </WbBadge>
        )}
      </span>
    </WbTooltip>
  ) : (
    <WbTooltip title={`${resultLabel} - ${value}`}>
      <span>
        {variant === 'reduced' ? (
          <Box style={style} className={clsx(classes.icon, classes.resultBox)}>
            {thresholdValueToString(value)}
          </Box>
        ) : (
          <WbBadge startIcon={thresholdValueToString(value)} {...badgeProps}>
            {capitalize(resultLabel)}
          </WbBadge>
        )}
      </span>
    </WbTooltip>
  );
};
